import { SyntheticEvent, useEffect } from 'react';
import styled from 'styled-components';
import { Tab, Tabs } from '@elementary/common-ui/components';
import { TabContext, TabPanel as TabPanelBase } from '@mui/lab';
import { Box } from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  PARENT_TEST_ID,
  TEST_OVERVIEW_SIDEPANEL_TAB,
  TEST_OVERVIEW_TAB_EXECUTION_INFO,
  TEST_OVERVIEW_TAB_TEST_RESULTS,
  TEST_OVERVIEW_TAB_TEST_CONFIGURATION,
} from '@elementary/common-ui/constants/urlParams';
import { DateRangeValue } from '@elementary/common-ui/components/DateRangePicker';
import { isElementaryCloudTest } from '@elementary/common-ui/utils/common';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import {
  Dependencies,
  DependenciesEmptyState,
} from 'shared/components/DependenciesTable';
import ExecutionHistory from 'shared/components/ExecutionHistory';
import TestOverviewExecutionDetails from 'shared/components/TestOverviewExecutionDetails';
import { NO_ASSETS_LINKED_TO_TEST_DOCS_LINK } from 'shared/constants';
import { SourceType } from 'shared/types';
import { TestOverview as TestOverviewType } from '../../types';
import useTestMetadataUpdate from '../../hooks/useTestMetadataUpdate';
import TestConfiguration from '../TestConfiguration';
import LatestResultsTab from './LatestResultsTab';
import TestOverviewHeader from './TestOverviewHeader';
import TestOverviewHeaderDetails from './TestOverviewHeaderDetails';

const TabPanel = styled(TabPanelBase)`
  height: 100%;
  padding: 0;
`;

type TabsType =
  | typeof TEST_OVERVIEW_TAB_EXECUTION_INFO
  | typeof TEST_OVERVIEW_TAB_TEST_CONFIGURATION;

type Props = {
  testOverview: TestOverviewType;
  allExecutions?: TestOverviewType[];
  testId: string;
  onFeedbackClick?: () => void;
  dateRange?: DateRangeValue;
};

const TestOverview = ({
  testOverview,
  allExecutions,
  testId,
  onFeedbackClick,
  dateRange,
}: Props) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const parentTestId = searchParams.get(PARENT_TEST_ID);
  const isTestManagementContext =
    location.pathname.includes('/test-management') && !parentTestId;

  const defaultTab = isTestManagementContext
    ? TEST_OVERVIEW_TAB_TEST_CONFIGURATION
    : TEST_OVERVIEW_TAB_EXECUTION_INFO;

  const urlTab = searchParams.get(TEST_OVERVIEW_SIDEPANEL_TAB);

  useEffect(() => {
    if (
      isTestManagementContext &&
      urlTab === TEST_OVERVIEW_TAB_EXECUTION_INFO
    ) {
      setSearchParams((prev) => {
        prev.set(
          TEST_OVERVIEW_SIDEPANEL_TAB,
          TEST_OVERVIEW_TAB_TEST_CONFIGURATION,
        );
        return prev;
      });
    }
  }, [isTestManagementContext, urlTab, setSearchParams]);

  const currentTab =
    isTestManagementContext && urlTab === TEST_OVERVIEW_TAB_EXECUTION_INFO
      ? TEST_OVERVIEW_TAB_TEST_CONFIGURATION
      : urlTab ?? defaultTab;

  const tableAssetId = testOverview?.test.tableAsset?.id;
  const isCloudTest = isElementaryCloudTest(testOverview.test.type);
  const hasPermission = useEnvPermission();
  const hasEditPermission = hasPermission('EDIT_TEST_CONFIGURATIONS');
  const isCloudTestEditable = isCloudTest && hasEditPermission;
  const latestResultsExecutions = allExecutions ?? [testOverview];

  const { updateOwners, updateTags, isUpdatingOwners, metadataOptions } =
    useTestMetadataUpdate({ isCloudTest });

  const resourceName = isTestManagementContext
    ? testOverview.test.tableAsset?.name || undefined
    : testOverview.execution.resource?.name;

  const resourceType = isTestManagementContext
    ? testOverview.test.tableAsset?.name
      ? 'TABLE'
      : undefined
    : testOverview.execution.resource?.type;

  return (
    <>
      <TestOverviewHeader
        resourceId={testOverview.test.tableAsset?.id}
        testName={testOverview.test.name}
        testType={testOverview.test.type}
        testSubType={testOverview.execution.subType}
        resourceName={resourceName}
        resourceType={resourceType}
        description={testOverview.test.description}
      />

      <TestOverviewHeaderDetails
        owners={testOverview.test.owners}
        subscribers={testOverview.test.subscribers}
        tags={testOverview.test.tags}
        tableAssetTags={testOverview.test.tableAsset?.tags ?? []}
        lastModificationTime={
          testOverview.test.tableAsset?.lastDataModificationTime
        }
        latestExecutionStatus={testOverview.execution.status || 'error'}
        hideStatus={isTestManagementContext}
        isEditable={isCloudTestEditable}
        ownerOptions={metadataOptions?.owners}
        tagOptions={metadataOptions?.tags}
        isUpdatingOwners={isUpdatingOwners}
        onUpdateOwners={updateOwners}
        onUpdateTags={updateTags}
      />

      <TabContext value={currentTab}>
        <Box sx={{ borderBottom: 1, borderColor: 'border.translucent' }}>
          <Tabs
            value={currentTab}
            onChange={(_event: SyntheticEvent, value: TabsType) =>
              setSearchParams((prev) => {
                prev.set(TEST_OVERVIEW_SIDEPANEL_TAB, value);
                return prev;
              })
            }
          >
            {!isTestManagementContext && (
              <Tab
                label="Execution Info"
                value={TEST_OVERVIEW_TAB_EXECUTION_INFO}
                wrapped
              />
            )}
            <Tab
              label="Test Configuration"
              value={TEST_OVERVIEW_TAB_TEST_CONFIGURATION}
              wrapped
            />
            {isTestManagementContext && (
              <Tab
                label="Test Results"
                value={TEST_OVERVIEW_TAB_TEST_RESULTS}
                wrapped
              />
            )}
            <Tab label="Dependencies" value="dependencies" wrapped />
          </Tabs>
        </Box>

        {!isTestManagementContext && (
          <TabPanel value={TEST_OVERVIEW_TAB_EXECUTION_INFO}>
            <ExecutionHistory
              DetailsComponent={TestOverviewExecutionDetails}
              id={testId}
              sourceType={SourceType.TEST}
              onFeedbackClick={onFeedbackClick}
              isCloudTest={isCloudTest}
              dateRange={dateRange}
            />
          </TabPanel>
        )}

        <TabPanel value={TEST_OVERVIEW_TAB_TEST_CONFIGURATION}>
          <TestConfiguration testOverview={testOverview} />
        </TabPanel>

        {isTestManagementContext && (
          <TabPanel value={TEST_OVERVIEW_TAB_TEST_RESULTS}>
            <LatestResultsTab
              executions={latestResultsExecutions}
              dateRange={dateRange}
            />
          </TabPanel>
        )}

        <TabPanel value="dependencies">
          {tableAssetId && testOverview.execution.resource ? (
            <Dependencies
              tableAssetId={tableAssetId}
              testName={testOverview.test.name}
              resource={testOverview.execution.resource}
            />
          ) : (
            <DependenciesEmptyState
              docsLink={NO_ASSETS_LINKED_TO_TEST_DOCS_LINK}
            />
          )}
        </TabPanel>
      </TabContext>
    </>
  );
};

export default TestOverview;
